import { useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
} from "@/components/ui/sidebar";

/**
 * TUT-472 — um grupo do menu no jeito "Menu recolhido" (só ícones).
 *
 * Recolhido, o submenu some (`group-data-[collapsible=icon]:hidden`, o padrão
 * do shadcn) e o trilho não abre ao passar o mouse (o "Menu híbrido", que
 * abria, saiu na TUT-521):
 * Pessoas, Prazos, Contas a Pagar, Frota… só eram alcançáveis pela URL, e
 * Transportes, que não tem página, não fazia nada ao clique. Aqui o ícone do
 * grupo abre os filhos ao lado, num popover com a cara do trilho; escolher um
 * destino fecha.
 *
 * TUT-478 (2b) — o ninho tem o desenho do menu EXPANDIDO: o pai é o cabeçalho
 * (o link da página do grupo, em negrito; sem página, só o nome) e os filhos
 * descem recuados sob a linha-guia do `SidebarMenuSub`. Antes o pai era mais
 * um item da lista e a guia estava zerada (sem margem, borda nem recuo à esquerda), então o
 * popover não dizia quem era filho de quem.
 *
 * `pagina` é o link do cabeçalho (ausente: o grupo não tem página) e
 * `children` são os filhos (`SidebarLink sub`) — quem monta é o `NavGroup`
 * do `AppShell`.
 */
export function GrupoRecolhido({
  icon: Icon,
  label,
  isActive,
  pagina,
  children,
}: {
  icon: LucideIcon;
  label: string;
  isActive: boolean;
  /** O link da página do grupo, já com o negrito; o cabeçalho sem página é só o nome. */
  pagina?: ReactNode;
  children: ReactNode;
}) {
  const [aberto, setAberto] = useState(false);
  return (
    <SidebarMenuItem>
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <SidebarMenuButton
            type="button"
            isActive={isActive}
            tooltip={label}
            className="min-h-[2.92rem] gap-[0.9rem] rounded-lg px-[0.85rem]"
          >
            <Icon aria-hidden="true" />
            <span>{label}</span>
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          aria-label={label}
          className="w-56 border-sidebar-border bg-sidebar p-2 text-sidebar-foreground"
          onClick={(event) => {
            if ((event.target as Element).closest("a")) setAberto(false);
          }}
        >
          <div
            data-slot="grupo-cabecalho"
            className="text-corpo font-semibold text-sidebar-foreground"
          >
            {pagina ?? (
              <div className="flex min-h-9 items-center gap-2 px-2.5 py-1.5">
                <Icon aria-hidden="true" className="size-4 shrink-0" />
                <span className="truncate">{label}</span>
              </div>
            )}
          </div>
          <SidebarMenuSub>{children}</SidebarMenuSub>
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  );
}
